import { FramerButton } from "@/components/shared";
import { CreateCreditLimitRequestInput } from "@/graphql/__generated__/graphql-operations";
import { ChangeEvent, Dispatch, FormEvent, JSX, SetStateAction, useState } from "react";
import { useCreateCreditLimitRequestMutationHook } from "@/hooks/mutations";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { CLRPageModals, CustomerPageModals } from "@/constants/enums";
import { useTranslation } from "react-i18next";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { handleValidation } from "@/utils/form";
import { Input } from "@/components/ui/input";

type Props = {
  showModal: CLRPageModals | CustomerPageModals;
  setShowModal: Dispatch<SetStateAction<CLRPageModals|CustomerPageModals>>;
  serviceProviderID: string;
  clrInput: CreateCreditLimitRequestInput|{ [x: string]: string|undefined; }|null;
  setInput: (_value: CreateCreditLimitRequestInput|{ [x: string]: string|undefined; }|null) => void;
}

/**
 * A modal component that allows the user to create a new Credit Limit Request
 * @param {Props} props
 * @returns {JSX.Element} CreditLimitNewRequest component
 */
function CreditLimitNewRequest(props: Props): JSX.Element {
  // Props
  const { showModal, setShowModal, serviceProviderID, clrInput, setInput } = props;

  // States
  const [hasErrors, setHasErrors] = useState(false);
  const [errors, setErrors] = useState<{
    requestedAmount: boolean;
    reasonForRequest: boolean;
  }>({
    requestedAmount: false,
    reasonForRequest: false,
  });

  // Hooks
  const { createCreditLimitRequestMutation } = useCreateCreditLimitRequestMutationHook();
  const { t } = useTranslation(["translation", "common"]);

  /**
  * onChange handler for input fields
  * @param {object} event - input event
  */
  function handleOnChange(event: ChangeEvent<HTMLInputElement> | ChangeEvent<HTMLTextAreaElement>, type: string) {
    // get the updated user input
    const { name, value } = event.target;
    const isValid = handleValidation(value, type);

    if(isValid) {
      setInput({...clrInput, [name]: value});
      setHasErrors(false);
      setErrors({...errors, [name]: false});
    }
    else {
      setErrors({...errors, [name]: true});
      setHasErrors(true);
    }
  }

  /**
   * Handler function for form submission
   * @param {FormEvent<Element>} e - form event
   */
  function handleFormSubmit(e: FormEvent<Element>){
    e.preventDefault();
    createCreditLimitRequestMutation({
      variables: {
        creditLimitRequestInput: {
          ...clrInput as CreateCreditLimitRequestInput,
          serviceproviderID: serviceProviderID,
        }
      }
    });
    setShowModal(CLRPageModals.NoModal as any);
  }

  const handleOpenChange = (open: boolean) => {
    if (!open) {
      setShowModal(CLRPageModals.NoModal as any);
    }
  };

  return (
    <Dialog open={showModal === CLRPageModals.NewRequest} onOpenChange={handleOpenChange}>
      <DialogContent className="px-6 max-w-xl max-h-[90vh] overflow-y-auto dark:bg-mfdarklight"
        onPointerDownOutside={(event) => {
          event.preventDefault();
        }}
      >
        <DialogHeader>
          <DialogTitle className="text-lg dark:text-white">New Request</DialogTitle>
        </DialogHeader>

        <form onSubmit={handleFormSubmit}>
          {/* // TODO Clean up translations */}
          {/* Main container */}
          <div className="flex-1 pt-2 overflow-y-auto">
            <div className="flex flex-col select-none">
              {/* Extra padding to prevent clipping of textarea component */}
              <div className="px-1">
                <Label htmlFor="reasonForRequest">{t("customeroverview.labels.reasonForReq")}</Label>
                <Textarea
                  className={`mt-1 ${errors.reasonForRequest ? "border-red-500 focus:border-red-500" : ""}`}
                  onChange={(event: ChangeEvent<HTMLTextAreaElement>) => handleOnChange(event, "text")}
                  placeholder={t("customeroverview.labels.reasonForReq").toString()}
                  name="reasonForRequest"
                  required={false}
                />
                {errors.reasonForRequest && <p className="text-red-500">{t("customeroverview.errorMessages.reasonInvalid")}</p>}
              </div>

              {/* Extra padding to prevent clipping of input component */}
              <div className="px-1 pb-1">
                <Label htmlFor="requestedAmount">{t("customeroverview.labels.requestedAmount")}</Label>
                <Input
                  className={`mt-1 ${errors.requestedAmount ? "border-red-500 focus:border-red-500" : ""}`}
                  onChange={(event: ChangeEvent<HTMLInputElement>) => handleOnChange(event, "number")}
                  placeholder={t("customeroverview.labels.requestedAmount").toString()}
                  name="requestedAmount"
                  type="number"
                  required
                />
                {errors.requestedAmount && <p className="text-red-500">{t("customeroverview.errorMessages.amountInvalid")}</p>}
              </div>
            </div>
          </div>

          <DialogFooter>
            <div className="flex justify-end mt-4 w-full">
              <FramerButton
                disabled={hasErrors}
                className="bg-mforange text-white hover:bg-mforange/80"
              >
                {t("common:buttons.submit")}
              </FramerButton>
            </div>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

export default CreditLimitNewRequest;
